import React from 'react';

import { Button, ModalCard } from '~components';
import Icon, { faFileCsv } from '~components/icon';
import { TableView } from '~components/table-view';

import columns, { columnsWithRetryable } from '~support-at-home/forms/budgets/bulkCopyFailureList';

import { BudgetFailureRow } from '~support-at-home/actions/budgets';

import { exportBulkCopyFailuresCsv } from './bulkCopyCsv';
import { buildSampleTableRows } from './sampleTableRows';

interface ComponentProps {
  /** Head slice then tail slice, in selection order, exactly as the API sends them. */
  failures: BudgetFailureRow[];
  /**
   * Heading suffix, rendered as `{n} Budget(s) {headingSuffix}` — the tense differs before the copy
   * ("Will Not Be Copied") and after it ("Not Copied").
   */
  headingSuffix: string;
  /** Distinguishes the preview and review instances: data-cy hooks, the table id, and the CSV filename. */
  idPrefix: string;
  /**
   * Show the "Can Retry" column and carry it into the CSV. Step 4 only — see columnsWithRetryable for why
   * step 3 leaves it out.
   */
  showRetryable?: boolean;
  firstCount: number;
  lastCount: number;
  /** Full not-copied count, independent of how many of those rows were actually sampled. */
  total: number;
}

/**
 * The "not copied" table shared by the preview (step 3) and review (step 4) steps of the bulk-copy wizard
 * (TAV-2649a). Budgets land here when the API could not copy them at all — most commonly because the
 * client's package no longer resolves to a funding source — as opposed to the needs_review rows, which DID
 * copy. One row per budget with the API's reason, so a coordinator can fix the data and retry just these.
 *
 * Renders nothing when there are no failures, so both callers can drop it in unconditionally.
 *
 * At step 4 the list covers BOTH not-copied buckets the API reports — budgets whose copy failed and can be
 * retried, and ids that never resolved to a budget at all — kept in one table with a "Can Retry" column
 * rather than split in two, because the coordinator's question is "what didn't copy", not "why not".
 */
const FailureList: React.FC<ComponentProps> = ({
  failures,
  headingSuffix,
  idPrefix,
  showRetryable = false,
  firstCount,
  lastCount,
  total,
}) => {
  if (failures.length === 0) return null;

  // `retryable` is absent on the preview's failures (nothing attempted yet), so undefined reads as retryable
  // — a row is only called out as terminal when the API has explicitly said so.
  const isRetryable = (failure: BudgetFailureRow): boolean => failure.retryable !== false;

  const rows = failures.map((failure) => ({
    ...failure,
    id: failure.budget_id,
    retry: isRetryable(failure) ? 'Yes' : 'No',
  }));
  // The heading reports `total`, not `failures.length`, so a sampled list still says how many budgets were
  // actually not copied: `failures` may only be the first-10/last-5 sample of a much larger not-copied set.
  const noun = total === 1 ? 'Budget' : 'Budgets';
  const tableRows = buildSampleTableRows(rows, firstCount, lastCount, total);
  const hasBreak = total > firstCount + lastCount;
  // The heading above always reports `total`, but this download only ever holds `failures` (the sample).
  // When the two diverge (`hasBreak`), the label and filename have to say so explicitly: the filename is
  // the only marker that survives once the file is sitting in a downloads folder, detached from the screen
  // that produced it.
  const exportFileBase = hasBreak ? `${idPrefix}-not-copied-sample` : `${idPrefix}-not-copied`;
  const exportButtonLabel = hasBreak
    ? `Download not-copied sample (first ${firstCount}, last ${lastCount})`
    : 'Download not-copied list';

  return (
    <ModalCard header={`${total} ${noun} ${headingSuffix}`} className="w-100 mt-3">
      {/* Height-capped + scrollable: see .bulk-copy-scroll-table in _claims.scss */}
      <div className="bulk-copy-scroll-table">
        <TableView
          id={`${idPrefix}-failure-table`}
          data={tableRows}
          columns={showRetryable ? columnsWithRetryable : columns}
          readonly
          striped
        />
      </div>
      {hasBreak && (
        <p className="text-muted small mt-1 mb-0">
          Showing the first {firstCount} and last {lastCount} of {total} budgets.
        </p>
      )}
      <div className="d-flex justify-content-end mt-3">
        <Button
          color="info"
          onClick={() =>
            exportBulkCopyFailuresCsv(
              failures.map((failure) => ({
                client_name: failure.client_name,
                budget_id: failure.budget_id,
                budget_reference: failure.ref,
                reason: failure.reason,
                retryable: isRetryable(failure),
              })),
              // Per-step filename so a preview download and a post-copy download don't overwrite each
              // other: bulk-copy-preview-not-copied-<date>.csv / bulk-copy-review-not-copied-<date>.csv.
              // Suffixed with `-sample` when the file only holds the sampled rows, not every not-copied
              // budget (see exportFileBase above).
              exportFileBase,
            )
          }
          dataCy={`${idPrefix}-failure-export-button`}
        >
          <Icon icon={faFileCsv} className="me-2" />
          {exportButtonLabel}
        </Button>
      </div>
    </ModalCard>
  );
};

export default FailureList;
